import { computed } from "vue";
import {
  DS_USER_AVATAR_BADGE_SIZE_MAP,
  DS_USER_AVATAR_ICON_SIZE_MAP,
  DS_USER_STATUS_BADGE_MAP,
} from "@/src/design_system/data_display/user/constants/configuration";
import {
  DS_USER_AVATAR_CLASSES,
  DS_USER_AVATAR_STATUS_TO_CLASS_MAP,
} from "@/src/design_system/data_display/user/constants/classes";
import { DSUserAvatarProps } from "@/src/design_system/data_display/user/types/avatarProps";
import { DS_USER_STATUS } from "@/src/design_system/data_display/user/types/status";
import { DS_SIZE } from "@/src/design_system/shared/types/size";

export const useDSUserAvatar = (props: Readonly<DSUserAvatarProps>) => {
  const contentType = computed(() => {
    if (props.src) return "image";
    if (props.text) return "text";
    return props.iconName === "user-group" ? "group" : "icon";
  });

  const status = computed(() => {
    if (contentType.value === "group") return DS_USER_STATUS.None;
    return Object.values(DS_USER_STATUS).includes(props.status) ? props.status : DS_USER_STATUS.None;
  });

  const avatarClasses = computed(() => [
    DS_USER_AVATAR_CLASSES.Root,
    `${DS_USER_AVATAR_CLASSES.Root}--${contentType.value}`,
    DS_USER_AVATAR_STATUS_TO_CLASS_MAP[status.value],
    `${DS_USER_AVATAR_CLASSES.Root}--${props.size}`,
  ]);

  const accessibilityAttributes = computed(() => {
    if (contentType.value === "image") return {};
    if (props.ariaLabel) return { role: "img", "aria-label": props.ariaLabel };
    return { "aria-hidden": true };
  });

  const statusBadgeProps = computed(() => {
    if (status.value === DS_USER_STATUS.None) return null;

    const { ariaLabelLocaleKey, ...configuration } = DS_USER_STATUS_BADGE_MAP[status.value];
    const size = DS_USER_AVATAR_BADGE_SIZE_MAP[props.size];
    const ariaLabel = window.EasyLocale.getLocale(ariaLabelLocaleKey);
    if (size === DS_SIZE.Large) return { ...configuration, ariaLabel, size };

    return {
      state: configuration.state,
      disabled: configuration.disabled,
      ariaLabel,
      size,
    };
  });

  return {
    accessibilityAttributes,
    avatarClasses,
    contentType,
    iconSize: computed(() => DS_USER_AVATAR_ICON_SIZE_MAP[props.size]),
    statusBadgeProps,
  };
};
